import { create } from 'zustand'

/**
 * Engagement permissions that are NOT host controls.
 *
 * This store used to hold all eleven flags. Nine of them belong to the Host
 * Controls dialog, the room owns those, and they moved to
 * `meeting-host-controls.store.ts`. What is left is the two the Engagement
 * Controls dialog adds on top and nothing else tracks — plain client state, the
 * way this store always worked.
 *
 * Local and unsynchronised, deliberately: the room has no representation for
 * either, so nothing would be on the other end of a broadcast. If these ever
 * need to hold for every participant, they belong in `host_controls` on
 * `RoomCoordinator` and then in the other store — not in a second sync path
 * bolted onto this one.
 *
 * The Engagement dialog's other five toggles (chat, reactions, audio, video,
 * screen share) are the SAME flags as the host-controls dialog's, so they read
 * and write `meeting-host-controls.store.ts`. Only these two are answered here.
 */
export type EngagementOnlyPermissions = {
  /** Participants can raise their hand. */
  allowHandRaise: boolean
  /** Participants can submit Q&A questions. */
  allowQuestions: boolean
}

type HostPermissionsState = EngagementOnlyPermissions & {
  /** Replace a single permission. */
  setPermission: <K extends keyof EngagementOnlyPermissions>(
    key: K,
    value: EngagementOnlyPermissions[K],
  ) => void
  /** Replace several permissions at once. */
  setPermissions: (patch: Partial<EngagementOnlyPermissions>) => void
  /** Reset to the defaults below. */
  reset: () => void
}

const defaults: EngagementOnlyPermissions = {
  allowHandRaise: true,
  allowQuestions: true,
}

export const useHostPermissionsStore = create<HostPermissionsState>((set) => ({
  ...defaults,
  setPermission: (key, value) => set({ [key]: value } as Partial<HostPermissionsState>),
  setPermissions: (patch) => set(patch),
  reset: () => set({ ...defaults }),
}))

/** Selector helpers — keep call sites lean and stable. */
export const selectCanRaiseHand = (s: HostPermissionsState) => s.allowHandRaise
export const selectCanAskQuestions = (s: HostPermissionsState) => s.allowQuestions
